import { REPORTS_CLIENT } from "../clients/reports.client";
import type {
    OrgPublicKey,
    AdminWrap,
    AdminsWithAccessResponse,
    OrgEncryptionSetupPayload,
    GrantAdminPayload,
    StartReencryptionPayload,
    ReencryptionChunkPayload,
    StartReencryptionResponse,
    BatchItemsPageResponse,
    PendingBatchResponse,
    AuditLogFilters,
    AuditLogResponse,
    RecoveryPayload,
    MigrateEnvelopePayload,
    LegacyItemsCountResponse,
    LegacyItemsPageResponse,
} from "~/domain/OrgEncryption";

export const setupOrgEncryption = async (payload: OrgEncryptionSetupPayload) => {
    try {
        const response = await REPORTS_CLIENT.post("/org-encryption/setup", payload);
        return response.data;
    } catch (error) {
        console.error("Error setting up org encryption:", error);
        throw error;
    }
};

export const getPublicKey = async (): Promise<OrgPublicKey | null> => {
    try {
        const response = await REPORTS_CLIENT.get<OrgPublicKey | null>("/org-encryption/public-key");
        return response.data ?? null;
    } catch (error) {
        console.error("Error fetching org public key:", error);
        throw error;
    }
};

export const grantAdmin = async (payload: GrantAdminPayload) => {
    try {
        const response = await REPORTS_CLIENT.post("/org-encryption/grant-admin", payload);
        return response.data;
    } catch (error) {
        console.error("Error granting admin encryption access:", error);
        throw error;
    }
};

export const revokeAdmin = async (userId: string) => {
    try {
        const response = await REPORTS_CLIENT.delete(`/org-encryption/revoke-admin/${userId}`);
        return response.data;
    } catch (error) {
        console.error("Error revoking admin encryption access:", error);
        throw error;
    }
};

export const getAdminWrap = async (): Promise<AdminWrap | null> => {
    try {
        const response = await REPORTS_CLIENT.get<AdminWrap | null>("/org-encryption/admin-wrap");
        return response.data ?? null;
    } catch (error) {
        console.error("Error fetching admin wrap:", error);
        throw error;
    }
};

export const getAdminsWithAccess = async (): Promise<AdminsWithAccessResponse> => {
    try {
        const response = await REPORTS_CLIENT.get<AdminsWithAccessResponse>("/org-encryption/admins-with-access");
        return response.data;
    } catch (error) {
        console.error("Error fetching admins with access:", error);
        throw error;
    }
};

export const startReencryption = async (payload: StartReencryptionPayload): Promise<StartReencryptionResponse> => {
    try {
        const response = await REPORTS_CLIENT.post<StartReencryptionResponse>(
            "/org-encryption/start-reencryption",
            payload,
        );
        return response.data;
    } catch (error) {
        console.error("Error starting re-encryption:", error);
        throw error;
    }
};

export const submitReencryptionChunk = async (payload: ReencryptionChunkPayload) => {
    try {
        const response = await REPORTS_CLIENT.post("/org-encryption/reencryption-chunk", payload);
        return response.data;
    } catch (error) {
        console.error("Error submitting re-encryption chunk:", error);
        throw error;
    }
};

export const recovery = async (payload: RecoveryPayload) => {
    try {
        const response = await REPORTS_CLIENT.post("/org-encryption/recovery", payload);
        return response.data;
    } catch (error) {
        console.error("Error performing recovery:", error);
        throw error;
    }
};

export const getAuditLog = async (filters: AuditLogFilters = {}): Promise<AuditLogResponse> => {
    try {
        const response = await REPORTS_CLIENT.get<AuditLogResponse>("/org-encryption/audit-log", {
            params: filters,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching audit log:", error);
        throw error;
    }
};

export const migrateEnvelope = async (payload: MigrateEnvelopePayload) => {
    try {
        const response = await REPORTS_CLIENT.post("/reports/migrate-envelope", payload);
        return response.data;
    } catch (error) {
        console.error("Error migrating envelope:", error);
        throw error;
    }
};


export const logRecoveryKitGenerated = async () => {
    const response = await REPORTS_CLIENT.post("/org-encryption/recovery-kit-generated", {});
    return response.data;
};

export const hasRecoveryKit = async (): Promise<{ has_kit: boolean }> => {
    const response = await REPORTS_CLIENT.get<{ has_kit: boolean }>("/org-encryption/has-recovery-kit");
    return response.data;
};

export const getPendingBatch = async (targetUserId: string): Promise<PendingBatchResponse | null> => {
    try {
        const response = await REPORTS_CLIENT.get<PendingBatchResponse | null>(
            `/org-encryption/pending-batch/${targetUserId}`,
        );
        return response.data ?? null;
    } catch (error) {
        console.error("Error fetching pending batch:", error);
        throw error;
    }
};

export const getBatchItemsPage = async (
    batchId: string,
    pageSize: number = 20,
): Promise<BatchItemsPageResponse> => {
    try {
        // Always fetch page 1 — completed items are excluded server-side,
        // so page 1 always returns the next pending items.
        const response = await REPORTS_CLIENT.get<BatchItemsPageResponse>(
            `/org-encryption/batch-items-page/${batchId}`,
            { params: { page: 1, page_size: pageSize } },
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching batch items page:", error);
        throw error;
    }
};


export const getLegacyItemsCount = async (): Promise<LegacyItemsCountResponse> => {
    try {
        const response = await REPORTS_CLIENT.get<LegacyItemsCountResponse>("/legacy-items-count");
        return response.data;
    } catch (error) {
        console.error("Error fetching legacy items count:", error);
        throw error;
    }
};

export const getLegacyItemsPage = async (
    page: number = 1,
    pageSize: number = 20,
): Promise<LegacyItemsPageResponse> => {
    try {
        const response = await REPORTS_CLIENT.get<LegacyItemsPageResponse>(
            "/legacy-items-page",
            { params: { page, page_size: pageSize } },
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching legacy items page:", error);
        throw error;
    }
};
